// src/langPath.js
// Single source of truth for path-based language prefixing.
//
// English lives at the root (/solutions) and stays exactly where it has always
// been - those URLs are already indexed, so they are never touched. The other
// languages are added underneath their own prefix (/hu/solutions,
// /ro/solutions, /de/solutions). Purely additive: no existing URL moves.
//
// The prefix is derived from window.location ONCE per page load, not from the
// active i18n language. That distinction matters: a Hungarian visitor landing on
// the English root gets Hungarian UI text by browser detection, but the page is
// still the English URL and must keep declaring the English canonical.
export const PREFIXES = { hu: '/hu', ro: '/ro', de: '/de' };
export const HU_PREFIX = PREFIXES.hu; // kept for existing imports

function detect() {
    try {
        const p = window.location.pathname;
        for (const [lang, prefix] of Object.entries(PREFIXES)) {
            if (p === prefix || p.startsWith(prefix + '/')) return lang;
        }
    } catch (e) {
        // no window (build tooling) - fall through
    }
    return null;
}

// 'hu' | 'ro' | 'de' when the URL is under that prefix, otherwise null (= root/English).
export const PATH_LANG = detect();

// The language this document represents, including the implicit English root.
export const TREE_LANG = PATH_LANG || 'en';

// Passed to <Router basename>. With basename="/hu" every existing
// <Link to="/solutions"> resolves to /hu/solutions automatically, so the
// internal links in the app need no changes at all.
export const BASENAME = PATH_LANG ? PREFIXES[PATH_LANG] : '/';

// Prefix for absolute URLs we emit ourselves (canonical, hreflang, sitemap).
export const URL_PREFIX = PATH_LANG ? PREFIXES[PATH_LANG] : '';

// All languages that have their own prerendered, indexable URL tree.
export const INDEXABLE_LANGS = ['en', 'hu', 'ro', 'de'];

export const prefixFor = (lang) => (lang === 'en' ? '' : (PREFIXES[lang] || ''));

// Absolute-path builder: urlFor('de', '/solutions') -> '/de/solutions'
export function urlFor(lang, path) {
    const clean = path === '/' ? '' : path;
    return `${prefixFor(lang)}${clean}` || '/';
}

// Language-specific slugs for the topic pillar page. Each language gets a
// natural localized slug rather than a translated path of the English one.
export const PILLAR_SLUGS = {
    en: '/industrial-ai',
    hu: '/ipari-mesterseges-intelligencia',
    ro: '/inteligenta-artificiala-industriala',
    de: '/industrielle-kuenstliche-intelligenz',
};
